import moment from 'moment';
import get from 'lodash/get';

// utils
import { alertOk } from 'components/paymentActionAlerts/PaymentActionAlerts.utils';
import { buildBankCode, buildAccountName } from 'components/wirePayment/utils';
import { arrayToObject } from 'utils/transformations';

import {
  createWirePayment as apiCreateWirePayment,
  modifyWirePayment as apiModifyWirePayment,
  createWireTemplate as apiCreateWireTemplate,
  modifyWireTemplate as apiModifyWireTemplate
} from 'api/payments/service';

import {
  PAYMENT_TYPES,
  API_DATE_FORMAT,
  API_ERROR,
  WORKFLOWS,
  WORKSPACES,
  PAYMENT_ACTIONS,
  PAYMENT_SOURCE
} from 'constants.js';
import { getApiError, getPaymentType } from './CreatePayment.utils';
import { mapValueBack, getAmountTypeOptions, getChargesOptions } from './CreatePayment.dropdowns';

/**
 * Format keys of BBI and OBI, to make appropriate payload keys
 * @param {object} data
 * @returns {object}
 */
const formatBBIAndOBIKeys = (data, prefix, replace = true) => {
  if (data) {
    const o = arrayToObject(
      Object.keys(data)
        .sort()
        .filter((x) => !x.indexOf(`${prefix}-`))
        .map((x) => ({ [replace ? x.replace(new RegExp(`${prefix}-`, 'g'), '') : x]: data[x] }))
    );

    return o && Object.keys(o).length > 0 ? o : null;
  }
  return null;
};

/**
 * Format originating account info, to match api
 *
 * @param {object} data
 * @returns {object}
 */
const formatOriginatingInfo = (data) => {
  const available = {
    currency: 'accountCurrency',
    id: 'accountId',
    name: 'accountName',
    number: 'accountNumber',
    nickname: 'accountNickName'
  };

  const keys = Object.keys(data).filter((x) => Object.keys(available).includes(x));

  return arrayToObject(
    keys.map((x) => ({
      [available[x]]: data[x]
    }))
  );
};

/**
 * Format amount to proper form
 * @param {number|string} amount
 * @returns {string}
 */
const formatAmount = (amount) =>
  amount ? parseFloat(parseFloat(String(amount).replace(/[,]/g, '')).toFixed(2)) : amount;

export const buildRetrievePaymentData = (data, options, locale) => {
  // dropdown options
  const amountTypes = getAmountTypeOptions();

  const getCurrency = options.fxRates.find((x) => [x.currency, x.id].includes(data.currency));
  const amountType = amountTypes.find((x) => x.type === data.amountType);

  const { beneficiaryBankInfo, intermediaryBankInfo1, intermediaryBankInfo2 } = data;

  const originatorId =
    get(data.originatingAccountInfo, 'accountId') || get(data.originatorAccountId, '0.id');
  const originator = options.paymentAccounts.find((x) => x.id === originatorId);
  const originatorAccountId = originator
    ? [
        {
          id: parseInt(originatorId, 10),
          name: buildAccountName(locale, originator),
          balance: originator.balance
        }
      ]
    : [];

  const opts = {
    ...data,
    originatorAccountId,
    currency: getCurrency ? getCurrency.id : null,
    amountType: (amountType && amountType.id) || data.amountType,
    transactionDate: data.transactionDate,
    amount: formatAmount(data.amount),
    // extra checks as data is coming as object with nulls sometimes
    beneficiaryBankInfo: get(beneficiaryBankInfo, 'bankName') && [
      buildBankCode(beneficiaryBankInfo)
    ],
    intermediaryBankInfo1: get(intermediaryBankInfo1, 'bankName') && [
      buildBankCode(intermediaryBankInfo1)
    ],
    intermediaryBankInfo2: get(intermediaryBankInfo2, 'bankName') && [
      buildBankCode(intermediaryBankInfo2)
    ],
    requiresApproval: data.remainingApprovals
  };

  return opts;
};

export const getAccurateValue = ({ field, force = false, form, matchBy, options, property }) => {
  const isStr = typeof form[field] === 'string' && Number.isNaN(parseInt(form[field], 10));
  const value =
    isStr && !force
      ? form[field]
      : mapValueBack(options, { form, type: field, matchBy: isStr ? matchBy : 'id' });

  return (property && value[property]) || value;
};

/**
 * Build payload used for wire payment api calls (Internationl and Domestic)
 * @param {object} fields all the data passed from the form
 */
export const buildWirePaymentPayload = (
  { action, copy, form, options, submit = true, template },
  viewable = false
) => {
  const type = get(
    form,
    'paymentType[0].type',
    typeof form.paymentType === 'string'
      ? form.paymentType
      : getPaymentType(form, options.paymentTypeOptions, {
          template,
          copy,
          property: template && !copy ? 'paymentType' : 'type'
        })
  );

  const currency =
    options.fxRates &&
    getAccurateValue({
      form,
      options: options.fxRates,
      field: 'currency',
      matchBy: 'currency',
      force: true
    });

  const comparator = get(form, 'amountType.0.id', get(form, 'amountType'));
  const amountType =
    options.amountTypes.find((x) => x.id === comparator || x.type === comparator) ||
    get(options.amountTypes, '0');

  const beneficiaryBankInfo = get(form, 'beneficiaryBankInfo.bankName')
    ? form.beneficiaryBankInfo
    : options.bankCodes &&
      buildBankCode(options.bankCodes.beneficiaryBankInfo) &&
      mapValueBack(options.bankCodes.beneficiaryBankInfo, {
        form,
        type: 'beneficiaryBankInfo.0.id',
        matchBy: 'routingNumber'
      });

  const intermediaryBankInfo1 = get(form, 'intermediaryBankInfo1.bankName')
    ? form.intermediaryBankInfo1
    : form.intermediaryBankInfo1 &&
      options.bankCodes.intermediaryBankInfo1 &&
      mapValueBack(options.bankCodes.intermediaryBankInfo1, {
        form,
        type: 'intermediaryBankInfo1.0.id',
        matchBy: 'routingNumber'
      });

  const intermediaryBankInfo2 = get(form, 'intermediaryBankInfo2.bankName')
    ? form.intermediaryBankInfo2
    : form.intermediaryBankInfo2 &&
      options.bankCodes.intermediaryBankInfo2 &&
      mapValueBack(options.bankCodes.intermediaryBankInfo2, {
        form,
        type: 'intermediaryBankInfo2.0.id',
        matchBy: 'routingNumber'
      });

  const originatingAccountInfo =
    options.paymentAccounts &&
    formatOriginatingInfo(
      mapValueBack(options.paymentAccounts, {
        form,
        type: 'originatorAccountId.0.id'
      })
    );

  const isInternational = type === PAYMENT_TYPES.WIRE.INTERNATIONAL;

  let payload = {
    amount: formatAmount(form.amount),
    bankToBankDetails: formatBBIAndOBIKeys(form, 'btb'),
    beneficiaryBankInfo,
    bankTrace: form.bankTrace,
    beneficiaryAccountInfo: {
      accountName: form.accountName,
      accountNumber: form.accountNumber
    },
    paymentDetails: formatBBIAndOBIKeys(form, 'pd'),
    originatingAccountInfo: {
      ...originatingAccountInfo,
      accountNumber: undefined
    },
    originatorAccountId: parseInt(originatingAccountInfo.accountId, 10),
    address1: form.address1,
    address2: form.address2,
    address3: form.address3,
    purpose: form.purpose,
    paymentType: type,
    entryMethod: PAYMENT_SOURCE.FREEFORM,
    transactionDate:
      (form.transactionDate && moment(form.transactionDate[0]).format(API_DATE_FORMAT)) || null,
    validateAndSave: submit
  };

  if (options.preferences) {
    const { freeFormIntermediaryBankTempl } = options.preferences;

    // intermediary
    if (freeFormIntermediaryBankTempl && freeFormIntermediaryBankTempl[type]) {
      payload = {
        ...payload,
        intermediaryBankInfo1:
          intermediaryBankInfo1 && Object.keys(intermediaryBankInfo1).length > 0
            ? intermediaryBankInfo1
            : null,
        intermediaryBankInfo2:
          intermediaryBankInfo2 && Object.keys(intermediaryBankInfo2).length > 0
            ? intermediaryBankInfo2
            : null
      };
    }
  }

  if (form.templateDetails || form.templateCode) {
    payload = {
      ...payload,
      templateCode: form?.templateDetails?.templateCode || form.templateCode
    };
  }

  if (isInternational) {
    const chargesOptions = getChargesOptions();
    payload = {
      ...payload,
      charges: get(
        chargesOptions.find((x) => x.id === get(form.charges, '0.id', get(form, 'charges'))),
        'id'
      ),
      currency: get(form, 'currency[0].currency', currency.currency)
    };

    if (viewable) {
      payload = {
        ...payload,
        currencyName: currency.currencyName
      };
    }
  }

  payload = {
    ...payload,
    amountType: viewable ? form.amountType : amountType.type,
    exchangeRate: form.exchangeRate,
    customRateConfirmation: form.customRateConfirmation
  };

  if (action === PAYMENT_ACTIONS.CREATE_TEMPLATE) {
    payload = {
      ...payload,
      currency: currency && currency.currency,
      templateName: form.templateName,
      transactionDate: null
    };
  }

  if ([PAYMENT_ACTIONS.VALIDATE_PAYMENT, PAYMENT_ACTIONS.CREATE_PAYMENT].includes(action)) {
    payload = {
      ...payload,
      bankTrace: ''
    };
  }

  if (action === PAYMENT_ACTIONS.MODIFY_TEMPLATE) {
    payload = {
      ...payload,
      currency: get(form, 'currency[0].currency'),
      transactionDate: null,
      templateCode: form.templateCode,
      templateName: form.templateName,
      amount: Number(payload.amount) ? payload.amount : null
    };
  }

  return payload;
};

/**
 * Build extra markup to make it working across different views.
 * @param {object} props
 * @param {object} props.instance
 * @param {array} props.accounts
 */
export const buildExtraMarkup = ({ accounts, instance }) => {
  const getOriginatingAccountInfo = () => {
    if (accounts) {
      const { name, number } = accounts.find((x) => x.id === instance.originatorAccountId);

      return {
        accountName: name,
        accountNumber: number
      };
    }

    return {
      accountName: instance.fromAccountName,
      accountNumber: instance.fromAccountNumber
    };
  };

  return {
    ...instance,
    originatingAccountInfo: getOriginatingAccountInfo()
  };
};

export const createWireTemplate = ({
  copy,
  form,
  history,
  locale,
  onError,
  onSubmission,
  options
}) => {
  const payload = buildWirePaymentPayload({
    form,
    copy,
    options,
    action: PAYMENT_ACTIONS.CREATE_TEMPLATE,
    template: true
  });
  apiCreateWireTemplate(payload).subscribe(({ data, result, success }) => {
    // If server error
    if (!success) {
      return onError(getApiError(locale, data, result));
    }

    onSubmission();

    return history.push({
      pathname: get(history, 'location.state.workflowStartPath', WORKSPACES.PYMTCNTR),
      state: {
        excludePathname: get(history, 'location.state.workflowStartPath', WORKSPACES.PYMTCNTR),
        previous: history.location.pathname,
        paymentTemplateAction: 'create',
        paymentTemplateActionSuccess: Array.isArray(data) ? data : [data],
        numberOfApprovalsRequired: form.approvalsRequired
      }
    });
  });
};

export const modifyWireTemplate = ({ form, history, locale, onError, options }) => {
  const payload = buildWirePaymentPayload({
    form,
    options,
    action: PAYMENT_ACTIONS.MODIFY_TEMPLATE
  });
  apiModifyWireTemplate(payload).subscribe(({ data, result, success }) => {
    // If server error
    if (!success) {
      return onError(getApiError(locale, data, result));
    }

    return history.push({
      state: {
        paymentTemplateActionSuccess: [data],
        paymentTemplateAction: 'Modified',
        excludePathname: get(history, 'location.state.workflowStartPath', WORKSPACES.PYMTCNTR)
      },
      pathname: get(history, 'location.state.workflowStartPath', WORKSPACES.PYMTCNTR)
    });
  });
};

export const modifyWirePayment = ({
  extra,
  form,
  history,
  locale,
  onError,
  onSubmission,
  options,
  setPaymentActionMessages
}) => {
  const returnUrl = get(history, 'location.state.workflowStartPath', WORKSPACES.PYMTCNTR);
  const payload = buildWirePaymentPayload({
    form,
    options,
    action: PAYMENT_ACTIONS.MODIFY_PAYMENT
  });
  apiModifyWirePayment(payload).subscribe(({ data, result, success }) => {
    // If server error
    if (!success) {
      // If failure response is from challenge manager
      if (data.challeangeable === true || get(data, '0.code') === API_ERROR.ACCOUNT_LOCKED) {
        return onError(data);
      }
      return onError(getApiError(locale, data, result));
    }

    // If successful
    setPaymentActionMessages([
      alertOk(locale, {
        title:
          data.remainingApprovals > 0
            ? locale['common.submittedForApproval']
            : locale['create.payment.successPayment'],
        paymentAction: 'modify',
        payment: data,
        extra
      })
    ]);

    onSubmission();

    return history.push({
      pathname: returnUrl,
      state: {
        previous: history.location.pathname
      }
    });
  });
};

export const validateWirePayment = ({
  action,
  callback,
  form,
  history,
  locale,
  onError,
  onSubmission,
  options
}) => {
  const isModify = action === PAYMENT_ACTIONS.MODIFY_PAYMENT;
  const isCopy =
    action === PAYMENT_ACTIONS.CREATE_PAYMENT && get(history, 'location.state.bankTrace');
  const payload = buildWirePaymentPayload({
    form,
    options,
    submit: false,
    action: isModify ? 'validate-modify' : PAYMENT_ACTIONS.VALIDATE_PAYMENT
  });

  payload.paymentType = get(
    form,
    'paymentType[0].type',
    getPaymentType(form, options.paymentTypeOptions)
  );

  const getRequest = isModify ? apiModifyWirePayment : apiCreateWirePayment;

  getRequest(payload).subscribe((res) => {
    const { data, result, success } = res;

    if (!success && data) return onError(getApiError(locale, data, result));
    if (success) {
      const { beneficiaryBankInfo, intermediaryBankInfo1, intermediaryBankInfo2 } = form;
      // save to store
      onSubmission({
        ...form,
        paymentDetails: payload.paymentDetails,
        bankToBankDetails: payload.bankToBankDetails,
        templateCode: payload.templateCode,
        amount: isModify ? form.amount : form.amount || form.creditAmount,
        amountType:
          form.amountType &&
          options.amountTypes.find(
            (x) => x.id === get(form.amountType, '0.id', get(form, 'amountType'))
          ).type,
        charges: get(form, 'charges.0.id', get(form, 'charges')),
        currency:
          (form.currency &&
            options.fxRates.find((x) => x.id === get(form, 'currency.0.id', get(form, 'currency')))
              .currency) ||
          locale['common.usd'],
        currencySuffix: get(form, 'currency[0].currency'),
        beneficiaryAccountInfo: {
          accountName: form.accountName,
          accountNumber: form.accountNumber
        },
        exchangeRate: get(form, 'exchangeRate'),
        originatingAccountInfo: formatOriginatingInfo(
          options.paymentAccounts.find((x) => x.id === get(form, 'originatorAccountId.0.id'))
        ),
        beneficiaryBankInfo: (options.bankCodes.beneficiaryBankInfo || []).find((x) =>
          [x.bankCode, x.routingNumber].includes(get(beneficiaryBankInfo, '0.id'))
        ),
        intermediaryBankInfo1: (options.bankCodes.intermediaryBankInfo1 || []).find((x) =>
          [x.bankCode, x.routingNumber].includes(get(intermediaryBankInfo1, '0.id'))
        ),
        intermediaryBankInfo2: (options.bankCodes.intermediaryBankInfo2 || []).find((x) =>
          [x.bankCode, x.routingNumber].includes(get(intermediaryBankInfo2, '0.id'))
        ),
        paymentType: payload.paymentType,
        options: {
          ...options
        },
        remainingApprovals: data.remainingApprovals || 0,
        entryMethod: data.entryMethod,
        wireEntryDate: data.wireEntryDate
      });
    }

    return callback({
      fn: () => {
        history.push({
          pathname: WORKFLOWS.REVIEW_TRANSFER,
          state: {
            action,
            isCopy: Boolean(isCopy),
            excludePathname: WORKFLOWS.REVIEW_TRANSFER,
            workflowStartPath: get(
              history,
              'location.state.workflowStartPath',
              WORKSPACES.PYMTCNTR
            ),
            preselect: get(history, 'location.state.preselect')
          }
        });
      }
    });
  });
};

export const createWirePayment = ({
  callback,
  clearPaymentData,
  form,
  locale,
  onError,
  options,
  setPaymentActionMessages
}) => {
  const payload = buildWirePaymentPayload({
    form,
    options,
    action: PAYMENT_ACTIONS.CREATE_PAYMENT
  });
  apiCreateWirePayment({ ...payload, validateAndSave: true }).subscribe((res) => {
    const { data, result, success } = res;

    if (!success && data) {
      // If failure response is from challenge manager
      if (data.challeangeable === true || data[0].code === API_ERROR.ACCOUNT_LOCKED)
        return onError(data);
      return getApiError(locale, data, result);
    }

    return callback({
      fn: () => {
        clearPaymentData();
        setPaymentActionMessages([
          alertOk(locale, {
            title:
              data.remainingApprovals > 0
                ? locale['common.submittedForApproval']
                : locale['create.payment.successPayment'],
            paymentAction: 'create',
            payment: data
          })
        ]);
      }
    });
  });
};
